import { FormattedMessage, history, useIntl } from "@umijs/max";
import { Button, Checkbox, message, Modal } from "antd";
import { FC, useEffect, useState } from "react";
import classNames from "classnames";
import { useDebounceFn, useRequest } from "ahooks";

import styles from "../index.less";

import { ReactComponent as TradePowerIcon } from "@/assets/images/trading-system/trade-power.svg";

import {
  ConsumptionType,
  createOrderService,
  OrderInfo,
  PayType,
  postBuyOrderPowerGoodsListService,
  postCanUsePowerDiscountService,
} from "@/services/order";
import { ResponseCode } from "@/constants";
import PayModal from "@/components/PayModal";
import { ServicePaymentAgreementUrl } from "@packages/pricing-components";

interface BuyPowerModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

/**
 * 购买动能值弹窗
 * @returns
 */
export const BuyPowerModal: FC<BuyPowerModalProps> = ({ open, setOpen }) => {
  const intl = useIntl();
  const { data: power } = useRequest(
    async () => {
      if (open) {
        // 查商品
        const rsp = await postBuyOrderPowerGoodsListService();
        // 查用户能否用折扣价格
        const canUseRsp = await postCanUsePowerDiscountService();

        if (rsp.code !== ResponseCode.Success) {
          message.error(rsp.msg);
          return [];
        }

        if (canUseRsp.code !== ResponseCode.Success) {
          message.error(canUseRsp.msg);
          return [];
        }

        // 可以用折扣就展示折扣价格，不可以用的话就干掉这个数据，进行合并渲染
        return (rsp.data ?? []).map((d) => ({
          ...d,
          discountAmount: canUseRsp.data ? d.discountAmount ?? void 0 : void 0,
        }));
      }

      return [];
    },
    {
      refreshDeps: [open],
    }
  );

  const [agreePolicy, setAgreePolicy] = useState<boolean>(false);
  const [pickedItem, setPickedItem] = useState<{
    amount: number;
    id: string;
  }>();
  const [openPayModal, setOpenPayModal] = useState<boolean>(false);
  const [orderInfo, setOrderInfo] = useState<OrderInfo>();

  const resetStates = () => {
    setAgreePolicy(false);
    setPickedItem(void 0);
    setOrderInfo(void 0);
  };

  useEffect(() => {
    if (power && power.length > 0) {
      // 后端写死的 11
      const defaultSelected = power.find(
        (p) => String(p.goodsId) === String(11)
      );

      if (defaultSelected) {
        setPickedItem({
          id: defaultSelected.goodsId!,
          amount: (defaultSelected.discountAmount ?? defaultSelected.amount)!,
        });
      }
    }
  }, [power]);

  const handleBuy = useDebounceFn(
    async () => {
      if (!agreePolicy) {
        message.info(
          intl.formatMessage({
            id: "components.trading-system.common.message.warn-check-policy",
          })
        );
        return;
      }

      const rsp = await createOrderService({
        power: pickedItem,
        powerNum: 1,
        consumptionType: ConsumptionType.FIRST,
      });

      if (rsp.code !== ResponseCode.Success) {
        message.error(rsp.msg);
        return;
      }

      if (rsp.data && rsp.data.amount > 0) {
        // 干掉当前弹窗
        resetStates();
        setOpen(false);
        // 唤醒支付
        setOrderInfo({
          orderId: rsp.data.orderId,
          amount: rsp.data.amount,
        });
        setOpenPayModal(true);
      }
    },
    {
      wait: 200,
    }
  );

  return (
    <>
      <Modal
        width={720}
        destroyOnClose
        classNames={{
          content: styles["ant-modal-content-wrapper"],
          header: styles["ant-modal-header-wrapper"],
          body: styles["ant-modal-body-wrapper"],
          footer: styles["ant-modal-footer-wrapper"],
        }}
        open={open}
        title={intl.formatMessage({ id: "components.buy-power-modal.title" })}
        maskClosable={false}
        onClose={() => {
          resetStates();
          setOpen(false);
        }}
        onCancel={() => {
          resetStates();
          setOpen(false);
        }}
        footer={
          <div className="flex flex-row justify-end items-center">
            <div className="flex flex-col justify-start items-end">
              <div className="flex flex-row justify-center items-end leading-none">
                <div className="mr-[4px] text-[14px] text-text-strong">
                  <FormattedMessage id="components.buy-power-modal.payment-amount" />
                </div>

                <div className="flex flex-row justify-end items-end text-[#EC7057] leading-none">
                  <div className="text-[14px]">¥</div>
                  <div className="text-[24px] font-[DIN]">
                    {pickedItem?.amount ?? "--"}
                  </div>
                </div>
              </div>
              <div className="mt-[6px] flex flex-row justify-center items-center">
                <Checkbox
                  className="mr-[4px]"
                  checked={agreePolicy}
                  onChange={(e) => setAgreePolicy(e.target.checked)}
                />
                <div className="text-text-regular">
                  <FormattedMessage id="components.trading-system.common.agreement" />
                </div>
                <a
                  href={ServicePaymentAgreementUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  <FormattedMessage id="components.trading-system.common.agreement.policy" />
                </a>
              </div>
            </div>

            <Button
              className="ml-[24px] w-[180px] h-[42px]"
              type="primary"
              size="large"
              disabled={!pickedItem}
              onClick={() => handleBuy.run()}
            >
              <FormattedMessage id="components.buy-power-modal.action.buy" />
            </Button>
          </div>
        }
      >
        <div className="mt-[36px] mb-[38px] grid grid-cols-3 gap-[24px] select-none">
          {power?.map((item) => (
            <div
              key={item.goodsId}
              className={classNames(
                "relative flex flex-col justify-center items-center rounded-lg border-[2px] cursor-pointer h-[120px]",
                pickedItem?.id === item.goodsId &&
                  "bg-primary-50 border-primary-200"
              )}
              onClick={() => {
                setPickedItem({
                  amount: (item.discountAmount ?? item.amount)!,
                  id: item.goodsId!,
                });
              }}
            >
              <div className="flex flex-row justify-center items-center">
                <TradePowerIcon />
                <div className="ml-[3px] text-[14px] text-text-regular">
                  {item.goodsName}
                </div>
              </div>

              {item.discountAmount !== void 0 ? (
                <div className="mt-4 flex flex-row">
                  <div className="flex flex-row items-end leading-none text-primary-800 mr-[4px]">
                    <div className="text-[16px]">¥</div>
                    <div className="font-[DIN] text-[32px]">
                      {item.discountAmount}
                    </div>
                  </div>
                  <div className="flex flex-row items-end leading-none text-text-secondary line-through text-[14px]">
                    <div className="text-[14px]">¥</div>
                    <div className="font-[DIN] text-[14px]">{item.amount}</div>
                  </div>
                </div>
              ) : (
                <div className="mt-4 flex flex-row items-end leading-none text-primary-800">
                  <div className="text-[16px]">¥</div>
                  <div className="font-[DIN] text-[32px]">{item.amount}</div>
                </div>
              )}
              {item.discountAmount !== void 0 && item.description && (
                <div
                  style={{
                    background:
                      "linear-gradient(265deg, #F89986 0%, #EC7057 100%)",
                  }}
                  className="h-[24px] w-[68px] py-[3px] px-[10px] absolute left-[-2px] top-[-8px] text-[12px] rounded-tl-xl rounded-br-xl text-white flex flex-row justify-center items-center"
                >
                  {item.description}
                </div>
              )}
            </div>
          ))}
        </div>
      </Modal>
      <PayModal
        open={openPayModal}
        onClose={() => {
          setOpenPayModal(false);
        }}
        orderInfo={orderInfo}
        onSuccess={(payType) => {
          setOpenPayModal(false);

          if (payType !== PayType.OFFLINE) {
            location.reload();
            return;
          }

          // 线下支付跳我的订单
          history.replace("/resource/order");
        }}
      />
    </>
  );
};

export default BuyPowerModal;
